import { Button, Flex, Link, Tag, useToast } from '@chakra-ui/react';
import { useListingforMlsListingLazyQuery } from '@client/graphql/__generated__/main-operations';
import {
  ListingForSearchMlsListingFragment,
  MlsListingFragment,
  PublicListingFragment,
} from '@client/graphql/__generated__/types';
import { ComponentProps } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getFormattedStatusText,
  getIsUserListing,
  getListingPrice,
} from '~/apps/consumer/utils/listing.utils';
import { getListingReportRoute } from '~/apps/consumer/utils/routes.utils';
import { EmbeddedConfig } from '~/apps/embedded/embedded-config';
import { useAppContext } from '~/services/main/contexts/AppContext';
import { useCreateBuyerOfferAnalysis } from '../../BuyerOfferAnalysis/hooks/useCreateBuyerOfferAnalysis';
import { PremiumTag } from '../../PremiumTag/PremiumTag';
import { BaseListingCard } from './BaseListingCard';

interface PublicListingCardProps
  extends Omit<ComponentProps<typeof BaseListingCard>, 'listing'> {
  listing?: ListingForSearchMlsListingFragment;
  mlsListing?: MlsListingFragment;
  isEmbedded?: boolean;
  fullWidth?: boolean;
  utmParams?: { [key: string]: string };
}

export const PublicListingCard = ({
  listing,
  mlsListing,
  isEmbedded = false,
  fullWidth = false,
  utmParams,
  ...otherProps
}: PublicListingCardProps) => {
  const navigate = useNavigate();
  const { user } = useAppContext();
  const [listingForMlsListing] = useListingforMlsListingLazyQuery();
  const toast = useToast();
  const { handleCreate, loading } = useCreateBuyerOfferAnalysis();

  const fetchPropertySlugAndNavigate = async () => {
    try {
      const { data } = await listingForMlsListing({
        variables: {
          input: {
            id: mlsListing?.id as string,
          },
        },
      });

      if (data?.syncListingFromMLS?.property?.slug) {
        const slug = data.syncListingFromMLS.property.slug;
        const listingReportRoute = getListingReportRoute(slug);
        const baseRoute = isEmbedded
          ? `${EmbeddedConfig.baseUrl}${listingReportRoute}`
          : listingReportRoute;

        const route = utmParams
          ? `${baseRoute}?${new URLSearchParams(utmParams).toString()}`
          : baseRoute;
        navigate(route);
      } else {
        toast({
          title: 'Could not find property',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Could not find property',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const onCardClick = () => {
    if (listing?.property?.slug) {
      const listingReportRoute = getListingReportRoute(listing.property.slug);
      navigate(listingReportRoute);
    } else if (!listing?.property?.slug && mlsListing) {
      void fetchPropertySlugAndNavigate();
    }
  };

  const onStartOfferScorecardClick = async () => {
    if (!listing?.id && !mlsListing?.id) {
      return;
    }

    const boa = await handleCreate({
      listingId: listing?.id,
      mlsListingId: mlsListing?.id,
    });
    if (boa?.id) {
      navigate(`/offer-scorecard/${boa.id}`);
    }
  };

  const buttons = (
    <Flex direction="column" gap={0}>
      <Button
        _hover={{
          textDecoration: 'none',
          bgColor: 'primaryButton.300', // Somehow setting textDecoration to none removes the hover bg color, so manually setting it
        }}
        as={Link}
        borderRadius="sm"
        fontSize="sm"
        isExternal={true}
        mt={2}
        size="xs"
        variant="solid"
        onClick={(e) => {
          e.stopPropagation();
          onCardClick();
        }}
      >
        See listing offer data
      </Button>
      <Button
        borderRadius="sm"
        fontSize="sm"
        isLoading={loading}
        mt={2}
        size="xs"
        variant="outline"
        onClick={(e) => {
          e.stopPropagation();
          void onStartOfferScorecardClick();
        }}
      >
        Create Offer Scorecard
      </Button>
    </Flex>
  );

  // display the mls status if only mls listing is provided
  // const offersCountDisplay =
  //   listing?.offersCountDisplay || mlsListing?.mls?.statusText;

  const isUserListing = getIsUserListing(
    listing as PublicListingFragment,
    user
  );

  const price = getListingPrice(mlsListing);

  const topTags = (
    <Flex alignItems="center" gap={1}>
      {isUserListing ? <PremiumTag label="My Listing" /> : undefined}
      {mlsListing?.mls?.statusText ? (
        <Tag lineHeight={1} variant="dark">
          {getFormattedStatusText(mlsListing?.mls?.statusText)}
        </Tag>
      ) : undefined}
    </Flex>
  );

  return (
    <>
      <BaseListingCard
        area={mlsListing?.property?.area}
        bathrooms={mlsListing?.property?.bathrooms}
        bedrooms={mlsListing?.property?.bedrooms}
        button={buttons}
        city={mlsListing?.address?.city}
        daysOnMarket={mlsListing?.mls?.daysOnMarket}
        fullWidth={fullWidth}
        office={mlsListing?.office?.name}
        photoSrc={mlsListing?.photos?.[0]}
        price={price}
        state={mlsListing?.address?.state}
        streetName={mlsListing?.address?.streetName}
        streetNumberText={mlsListing?.address?.streetNumberText}
        topTags={topTags}
        unit={mlsListing?.address?.unit}
        zipCode={mlsListing?.address?.postalCode}
        {...otherProps}
      />
    </>
  );
};
